import { Link2, Link2Off } from 'lucide-react'
import { Button } from '@components/ui/button'
import { cn } from '@/utils'
import type { UnitOfMeasure } from '@/lib/units/unit-conversion'
import type {
  DimensionField,
  DimensionValues,
  PanelFieldErrors,
  PositionField,
  PositionValues,
} from '../types/panel-fields'
import { TransformField } from './transform-field'

interface TransformFieldsProps {
  position: PositionValues
  dimensions: DimensionValues
  fieldErrors: PanelFieldErrors
  dimensionsLinked: boolean
  onDimensionsLinkedChange: (linked: boolean) => void
  onPositionChange: (field: PositionField, value: string) => void
  onPositionCommit: (field: PositionField, value: string) => void
  onDimensionChange: (field: DimensionField, value: string) => void
  onDimensionCommit: (field: DimensionField, value: string) => void
  disabled: boolean
  unitOfMeasure: UnitOfMeasure
  unitLabel: string
}

/** Position (X/Y) and Dimensions (W/H) grid with the constrain-proportions link button. */
export function TransformFields({
  position,
  dimensions,
  fieldErrors,
  dimensionsLinked,
  onDimensionsLinkedChange,
  onPositionChange,
  onPositionCommit,
  onDimensionChange,
  onDimensionCommit,
  disabled,
  unitOfMeasure,
  unitLabel,
}: TransformFieldsProps) {
  const positionRowHasError = !!(fieldErrors.x || fieldErrors.y)
  const dimensionRowHasError = !!(fieldErrors.width || fieldErrors.height)
  const shared = { disabled, unitOfMeasure, unitLabel }

  return (
    <div className="grid grid-cols-[1fr_16px_1fr] gap-2 auto-rows-auto">
      {/* Position - Row 1, Col 1 */}
      <TransformField
        label="Position"
        letter="X"
        value={position.x}
        error={fieldErrors.x}
        rowHasError={positionRowHasError}
        errorId="object-panel-x-error"
        ariaLabel="X position"
        onChange={(value) => onPositionChange('x', value)}
        onCommit={(value) => onPositionCommit('x', value)}
        {...shared}
      />

      {/* Empty space - Row 1, Col 2 */}
      <div />

      {/* Y Position - Row 1, Col 3 */}
      <TransformField
        letter="Y"
        value={position.y}
        error={fieldErrors.y}
        rowHasError={positionRowHasError}
        errorId="object-panel-y-error"
        ariaLabel="Y position"
        onChange={(value) => onPositionChange('y', value)}
        onCommit={(value) => onPositionCommit('y', value)}
        {...shared}
      />

      {/* Dimensions - Row 2, Col 1 */}
      <TransformField
        label="Dimensions"
        letter="W"
        value={dimensions.width}
        error={fieldErrors.width}
        rowHasError={dimensionRowHasError}
        errorId="object-panel-width-error"
        ariaLabel="Width"
        onChange={(value) => onDimensionChange('width', value)}
        onCommit={(value) => onDimensionCommit('width', value)}
        {...shared}
      />

      {/* Link Icon - Row 2, Col 2 - pt-7 aligns with input (label + gap-2) */}
      <div className="flex items-start justify-center pt-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDimensionsLinkedChange(!dimensionsLinked)}
          className={cn('h-8 w-8', dimensionsLinked ? 'text-foreground' : 'text-muted-foreground')}
          disabled={disabled}
          aria-label="Constrain Width and Height"
          aria-pressed={dimensionsLinked}
        >
          {dimensionsLinked ? <Link2 className="h-4 w-4" /> : <Link2Off className="h-4 w-4" />}
        </Button>
      </div>

      {/* Height Dimension - Row 2, Col 3 */}
      <TransformField
        letter="H"
        value={dimensions.height}
        error={fieldErrors.height}
        rowHasError={dimensionRowHasError}
        errorId="object-panel-height-error"
        ariaLabel="Height"
        onChange={(value) => onDimensionChange('height', value)}
        onCommit={(value) => onDimensionCommit('height', value)}
        {...shared}
      />
    </div>
  )
}
